import React, { useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Stack,
	Typography,
	Paper,
	List,
	ListItem,
	ListItemText,
	ListItemButton,
	IconButton,
	Box,
	CircularProgress,
} from '@mui/material';
import { AddCircle, Refresh } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { ServiceConnectionClient, ServiceConnectionModel, ServiceConnectionModelPagingResultModel } from '../../../api/api';
import useApiClient from '../../../common/hooks/useApiClient';
import CreateServiceConnectionDialog from '../CreateServiceConnectionDialog';

interface ServiceConnectionSelectionDialogProps {
	open: boolean;
	onClose: () => void;
	onSelect: (serviceConnection: ServiceConnectionModel) => void;
	serviceConnectionType?: string;
	serviceConnectionCategory?: string;
	title?: string;
	excludeIds?: string[];
}

export default function ServiceConnectionSelectionDialog({
	open,
	onClose,
	onSelect,
	serviceConnectionType,
	serviceConnectionCategory,
	title = 'Select Service Connection',
	excludeIds = [],
}: ServiceConnectionSelectionDialogProps) {
	const client = useApiClient(ServiceConnectionClient);
	const [openCreateDialog, setOpenCreateDialog] = useState(false);

	const { data, isLoading, refetch } = useQuery<ServiceConnectionModelPagingResultModel>({
		queryKey: ['serviceConnections', serviceConnectionType, serviceConnectionCategory],
		queryFn: async () => client.search({ type: serviceConnectionType, category: serviceConnectionCategory }),
		enabled: open,
	});

	const handleSelect = (serviceConnection: ServiceConnectionModel) => {
		onSelect(serviceConnection);
		// Note: Dialog closing is handled by the parent component
	};

	const handleCreateSuccess = () => {
		refetch();
		setOpenCreateDialog(false);
	};

	// Filter out already associated service connections
	const availableConnections = data?.items?.filter((sc) => !excludeIds.includes(sc.id)) || [];

	return (
		<>
			<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
				<DialogTitle>
					<Stack direction="row" justifyContent="space-between" alignItems="center">
						<Typography variant="h6">{title}</Typography>
						<Stack direction="row" spacing={1}>
							<IconButton size="small" onClick={() => refetch()} title="Refresh service connections">
								<Refresh />
							</IconButton>
							<Button variant="outlined" size="small" startIcon={<AddCircle />} onClick={() => setOpenCreateDialog(true)} title="Create new service connection">
								Create New
							</Button>
						</Stack>
					</Stack>
				</DialogTitle>

				<DialogContent>
					{(serviceConnectionCategory ?? serviceConnectionType) && (
						<Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
							Showing {serviceConnectionCategory ?? serviceConnectionType} service connections
						</Typography>
					)}

					{isLoading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
							<CircularProgress />
						</Box>
					) : availableConnections.length === 0 ? (
						<Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
							<Typography variant="body2" color="textSecondary">
								{excludeIds.length > 0 && data?.items?.length === excludeIds.length
									? 'All available service connections are already selected.'
									: `No ${serviceConnectionType ?? 'matching'} service connections found.`}
							</Typography>
							<Button variant="outlined" startIcon={<AddCircle />} onClick={() => setOpenCreateDialog(true)} sx={{ mt: 2 }}>
								Create New Service Connection
							</Button>
						</Paper>
					) : (
						<List sx={{ maxHeight: 400, overflow: 'auto' }}>
							{availableConnections.map((serviceConnection) => (
								<ListItem key={serviceConnection.id} disablePadding>
									<ListItemButton onClick={() => handleSelect(serviceConnection)}>
										<ListItemText
											primary={serviceConnection.name}
											secondary={
												<Stack direction="row" spacing={2}>
													<Typography variant="caption">Type: {serviceConnection.type}</Typography>
													<Typography variant="caption">Status: {serviceConnection.statusName}</Typography>
												</Stack>
											}
										/>
									</ListItemButton>
								</ListItem>
							))}
						</List>
					)}
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>Cancel</Button>
				</DialogActions>
			</Dialog>

			<CreateServiceConnectionDialog open={openCreateDialog} setOpen={setOpenCreateDialog} onSuccess={handleCreateSuccess} />
		</>
	);
}
